import React from 'react'
import * as R from 'ramda'
import { t } from 'ttag'

import { Box } from 'components/layout/Box/Box'
import { DeleteJobModal } from 'components/modals/DeleteJobModal/DeleteJobModal'
import { PositionManagementModal } from 'components/modals/PositionManagementModal/PositionManagementModal'
import { CreateTemplateModal } from 'components/modals/CreateTemplateModal/CreateTemplateModal'
import { ActionsDropdown } from 'components/shared/ActionsDropdown/ActionsDropdown'
import { HideExtensiveText } from 'components/shared/HideExtensiveText/HideExtensiveText'
import { Button } from 'components/shared/Button/Button'
import { Detail } from 'components/shared/Detail/Detail'
import { Pane } from 'components/shared/Pane/Pane'
import { ADMIN_ROLE } from 'constants/users'

import {
  getCompetenciesLabels,
  getLanguageLabels,
} from 'components/modals/PositionTemplateModal/common/TemplateDetails/model'
import { useJobDetailsTab } from './hooks'

import {
  Edit3Icon,
  MessageCircleIcon,
  PlusCircleIcon,
} from 'components/shared/Icon/model'

import { PaneTitle } from '../PaneTitle/PaneTitle'

import styles from './Details.module.scss'

const formatUser = ({ first_name, last_name, role }) =>
  `${first_name} ${last_name}${role === ADMIN_ROLE ? ' (Admin)' : ''}`

const sortAdminFirst = (a, b) => b.role - a.role

export const Details = ({ project }) => {
  const {
    description,
    department,
    id,
    interview_template,
    languages,
    name,
    profile,
    active,
  } = project ?? {}

  const hasCategories = R.any(key => R.equals(key, R.T()))(
    R.values(profile?.categories),
  )

  const { state, handlers } = useJobDetailsTab({ id })

  const {
    actions,
    language,
    isDeleteJobModalOpen,
    isEditPositionModalOpen,
    isCreateTemplateModalOpen,
  } = state
  const {
    handleDeleteJobModalClose,
    handleDeleteJobModalConfirm,
    handleEditPositionModalClose,
    handleEditPositionModalConfirm,
    handleEditPositionModalOpen,
    handleCreateTemplateModalClose,
    handleCreateTemplateModalConfirm,
    handleCreateTemplateModalOpen,
  } = handlers

  const outputVariables = profile?.outputVariables ?? {}

  const prioritizedOutcomeLabel = outputVariables.isProductivityEnabled
    ? t`High productivity`
    : outputVariables?.isSicknessAbsenceEnabled
    ? t`Low sickness absence`
    : outputVariables?.isTurnoverEnabled
    ? t`Low turnover`
    : t`No priority`

  return (
    <>
      <Pane.Header
        className={styles.header}
        title={<PaneTitle label={name} active={active} />}
      >
        <Button
          className={styles.button}
          color="primary"
          icon={Edit3Icon}
          label={t`Edit`}
          isDisabled={!active}
          size="small"
          variant="filled"
          onClick={handleEditPositionModalOpen}
        />
        <Button
          className={styles.button}
          label={t`Create template`}
          icon={PlusCircleIcon}
          isDisabled={!active}
          variant="outlined"
          size="small"
          onClick={handleCreateTemplateModalOpen}
        />
        <ActionsDropdown size="small" isDisabled={!active} {...{ actions }} />
      </Pane.Header>
      <Box className={styles.content}>
        <Box className={styles.main}>
          <Detail>
            <Detail.LabelWithTooltip>{t`Name`}</Detail.LabelWithTooltip>
            <Detail.Content type="text">{name}</Detail.Content>
          </Detail>
          <Detail>
            <Detail.LabelWithTooltip>{t`Department`}</Detail.LabelWithTooltip>
            <Detail.Content type="text">{department?.name}</Detail.Content>
          </Detail>
          {languages ? (
            <Detail>
              <Detail.LabelWithTooltip
                tooltip={t`Selected language(s) will be available as options for candidate invitation and test language.`}
              >{t`Languages`}</Detail.LabelWithTooltip>
              <Detail.Content type="text">
                {getLanguageLabels(languages)}
              </Detail.Content>
            </Detail>
          ) : null}
          {interview_template ? (
            <Detail>
              <Detail.LabelWithTooltip
                tooltip={t`The job template has an interview template connected to it.`}
              >
                {t`Interview`}
              </Detail.LabelWithTooltip>
              <Detail.Content icon={MessageCircleIcon} type="iconWithText">
                {interview_template}
              </Detail.Content>
            </Detail>
          ) : null}
          {hasCategories ? (
            <Detail>
              <Detail.LabelWithTooltip
                tooltip={t`The selected competencies that are most relevant to the job requirements. They determine how the calculation of test scores will be adjusted to favour relevant personality factors while the content of the candidate's tests remains unchanged.`}
              >
                {t`Most fitting competencies`}
              </Detail.LabelWithTooltip>
              <Detail.Content
                chips={getCompetenciesLabels(profile?.categories, language)}
                type="chipsGroup"
              />
            </Detail>
          ) : null}
          {description ? (
            <Detail>
              <Detail.LabelWithTooltip>{t`Description`}</Detail.LabelWithTooltip>
              <Detail.Content type="text">{description}</Detail.Content>
            </Detail>
          ) : null}
          {profile?.outputVariables ? (
            <Detail>
              <Detail.LabelWithTooltip
                tooltip={t`Organisational goals important for the position and your organisation`}
              >
                {t`Prioritised outcome`}
              </Detail.LabelWithTooltip>
              <div className={styles.prioritizedContainer}>
                <Detail.Content type="text">
                  {prioritizedOutcomeLabel}
                </Detail.Content>
              </div>
            </Detail>
          ) : null}
          {project?.members_with_access?.length ? (
            <Detail>
              <Detail.LabelWithTooltip
                tooltip={t`Users that have access to this job`}
              >
                {t`User access`}
              </Detail.LabelWithTooltip>
              <Detail.Content type="text" textClassName={styles.limitWidth}>
                <HideExtensiveText
                  length={140}
                  showReadLess={false}
                  text={project.members_with_access
                    .sort(sortAdminFirst)
                    .map(formatUser)
                    .join(', ')}
                />
              </Detail.Content>
            </Detail>
          ) : null}
        </Box>
      </Box>
      <PositionManagementModal
        template={project}
        onCancel={handleEditPositionModalClose}
        onClose={handleEditPositionModalClose}
        onConfirm={handleEditPositionModalConfirm}
        isOpen={isEditPositionModalOpen}
      />
      <CreateTemplateModal
        project={{ ...project, department: department?.id }}
        onCancel={handleCreateTemplateModalClose}
        onClose={handleCreateTemplateModalClose}
        onConfirm={handleCreateTemplateModalConfirm}
        isOpen={isCreateTemplateModalOpen}
      />
      <DeleteJobModal
        onCancel={handleDeleteJobModalClose}
        onClose={handleDeleteJobModalClose}
        onConfirm={handleDeleteJobModalConfirm}
        isOpen={isDeleteJobModalOpen}
        {...{ name }}
      />
    </>
  )
}
